import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { fetchCurrentUser } from '../../ducks/user.duck';

// Helper to extract payin amount
const extractTransactionPayin = tx => {
  if (!tx || !tx.attributes) return 0;
  const payinTotal = tx.attributes.payinTotal;
  if (payinTotal && typeof payinTotal.amount === 'number') {
    return payinTotal.amount / 100;
  }
  return 0;
};

// Async thunk to fetch all customer dashboard data in parallel
export const fetchCustomerDashboardDataThunk = createAsyncThunk(
  'CustomerDashboardPage/fetchCustomerDashboardData',
  async (config, { getState, extra: sdk, rejectWithValue }) => {
    try {
      const state = getState();
      const currentUser = state.user?.currentUser;
      const currentUserId = currentUser?.id?.uuid || currentUser?.id;

      const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
      } = config?.layout?.listingImage || {};
      const aspectRatio = aspectHeight / aspectWidth;

      // 1. Fetch customer's orders (transactions where only: 'order')
      const ordersPromise = sdk.transactions.query({
        only: 'order',
        perPage: 100,
        include: [
          'provider',
          'provider.profileImage',
          'listing',
          'listing.images',
          'booking',
        ],
        'fields.image': [
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
          'variants.square-small',
          'variants.square-small2x',
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      });

      // 2. Fetch customer's written reviews
      const reviewsPromise = currentUserId
        ? sdk.reviews.query({
            author_id: currentUserId,
            state: 'public',
            include: ['subject', 'subject.profileImage'],
            'fields.image': ['variants.square-small', 'variants.square-small2x'],
          })
        : Promise.resolve({ data: { data: [] } });

      const [ordersRes, reviewsRes] = await Promise.all([
        ordersPromise,
        reviewsPromise,
      ]);

      // Denormalize orders & reviews
      const orders = denormalisedResponseEntities(ordersRes);
      const reviews = denormalisedResponseEntities(reviewsRes);

      // Compute counts & totals
      let activeOrdersCount = 0;
      let completedOrdersCount = 0;
      let totalSpentAmount = 0;
      let currency = 'USD';

      orders.forEach(tx => {
        const lastTransition = (tx.attributes?.lastTransition || '').toLowerCase();
        const isCancelled =
          lastTransition.includes('cancel') ||
          lastTransition.includes('decline') ||
          lastTransition.includes('expire');
        const isCompleted =
          lastTransition.includes('complete') ||
          lastTransition.includes('delivered') ||
          lastTransition.includes('reviewed') ||
          lastTransition.includes('review-');

        if (!isCancelled) {
          totalSpentAmount += extractTransactionPayin(tx);
          if (tx.attributes?.payinTotal?.currency) {
            currency = tx.attributes.payinTotal.currency;
          }

          if (isCompleted) {
            completedOrdersCount += 1;
          } else {
            activeOrdersCount += 1;
          }
        }
      });

      const ordersCount = orders.length;
      const reviewsCount = reviews.length;

      // Build Recent Activity Feed for Customer
      const activityItems = [];

      orders.slice(0, 15).forEach(tx => {
        const createdAt = tx.attributes?.createdAt;
        const provider = tx.provider;
        const sellerName =
          provider?.attributes?.profile?.displayName ||
          provider?.attributes?.profile?.firstName ||
          'Seller';
        const listing = tx.listing;
        const listingTitle = listing?.attributes?.title || 'Order Item';
        const listingImages = listing?.images || [];
        const listingImage =
          listingImages[0]?.attributes?.variants?.['listing-card']?.url ||
          listingImages[0]?.attributes?.variants?.['square-small']?.url;
        const lastTransition = (tx.attributes?.lastTransition || '').toLowerCase();
        const payinTotal = tx.attributes?.payinTotal;
        const formattedPrice = payinTotal
          ? `${payinTotal.currency === 'EUR' ? '€' : '$'}${(payinTotal.amount / 100).toFixed(2)}`
          : null;

        if (lastTransition.includes('complete') || lastTransition.includes('delivered')) {
          activityItems.push({
            id: `completed-${tx.id?.uuid || tx.id}`,
            txId: tx.id?.uuid || tx.id,
            type: 'order_completed',
            title: `Order completed: ${listingTitle}`,
            sellerName,
            listingTitle,
            listingImage,
            formattedPrice,
            status: 'Completed',
            date: createdAt,
          });
        } else if (lastTransition.includes('cancel') || lastTransition.includes('decline')) {
          activityItems.push({
            id: `cancelled-${tx.id?.uuid || tx.id}`,
            txId: tx.id?.uuid || tx.id,
            type: 'order_cancelled',
            title: `Order cancelled: ${listingTitle}`,
            sellerName,
            listingTitle,
            listingImage,
            formattedPrice,
            status: 'Cancelled',
            date: createdAt,
          });
        } else {
          activityItems.push({
            id: `order-${tx.id?.uuid || tx.id}`,
            txId: tx.id?.uuid || tx.id,
            type: 'order_placed',
            title: `Order placed: ${listingTitle}`,
            sellerName,
            listingTitle,
            listingImage,
            formattedPrice,
            status: 'In Progress',
            date: createdAt,
          });
        }

        if (sellerName) {
          activityItems.push({
            id: `msg-${tx.id?.uuid || tx.id}`,
            txId: tx.id?.uuid || tx.id,
            type: 'message_thread',
            title: `Message with ${sellerName}`,
            sellerName,
            listingTitle,
            listingImage,
            status: 'Message',
            date: createdAt,
          });
        }
      });

      reviews.slice(0, 5).forEach(rev => {
        const subject = rev.subject;
        const subjectName =
          subject?.attributes?.profile?.displayName ||
          subject?.attributes?.profile?.firstName ||
          'Seller';

        activityItems.push({
          id: `review-${rev.id?.uuid || rev.id}`,
          type: 'review_given',
          title: `You reviewed ${subjectName}`,
          rating: rev.attributes?.rating || 5,
          content: rev.attributes?.content || '',
          date: rev.attributes?.createdAt,
        });
      });

      activityItems.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      const recentActivity = activityItems.slice(0, 50);

      return {
        ordersCount,
        activeOrdersCount,
        completedOrdersCount,
        totalSpentAmount,
        currency,
        reviewsCount,
        recentActivity,
        rawOrders: orders,
        rawReviews: reviews,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// Redux Slice
const customerDashboardSlice = createSlice({
  name: 'CustomerDashboardPage',
  initialState: {
    fetchInProgress: false,
    fetchError: null,
    ordersCount: 0,
    activeOrdersCount: 0,
    completedOrdersCount: 0,
    totalSpentAmount: 0,
    currency: 'USD',
    reviewsCount: 0,
    recentActivity: [],
    rawOrders: [],
    rawReviews: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCustomerDashboardDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchCustomerDashboardDataThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.ordersCount = action.payload.ordersCount;
        state.activeOrdersCount = action.payload.activeOrdersCount;
        state.completedOrdersCount = action.payload.completedOrdersCount;
        state.totalSpentAmount = action.payload.totalSpentAmount;
        state.currency = action.payload.currency;
        state.reviewsCount = action.payload.reviewsCount;
        state.recentActivity = action.payload.recentActivity;
        state.rawOrders = action.payload.rawOrders;
        state.rawReviews = action.payload.rawReviews;
      })
      .addCase(fetchCustomerDashboardDataThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default customerDashboardSlice.reducer;

// SSR loadData function
export const loadData = (params, search, config) => dispatch => {
  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(fetchCustomerDashboardDataThunk(config)),
  ]);
};
